/**
 * UserModal Component
 * Modal for creating and editing users
 */

import { useState, useEffect } from 'react'
import { X, User, Mail, Phone, Shield, AlertCircle } from 'lucide-react'
import type { User as UserType, UserRole } from '@/types/user.types'
import { getRoleDisplayName } from '@/types/auth.types'
import { PasswordField } from '@/components/ui/PasswordField'

// ============================================================================
// TYPES
// ============================================================================

interface UserModalProps {
  isOpen: boolean
  onClose: () => void
  onSubmit: (data: UserFormData) => Promise<void>
  user?: UserType | null
  mode: 'create' | 'edit'
  availableRoles?: UserRole[]
}

export interface UserFormData {
  email: string
  fullName: string
  phone?: string
  role: UserRole
  isActive?: boolean
  /** Temporary password set by the admin; collected in create mode only. */
  password?: string
}

/** Minimum password length accepted by the security-service create endpoint. */
const MIN_PASSWORD_LENGTH = 8

// ============================================================================
// CONSTANTS
// ============================================================================

const DEFAULT_ROLES: UserRole[] = [
  'firm_admin',
  'ca_partner',
  'technical_assistant',
  'research_assistant',
  'individual_client',
  'business_owner_client',
  'authorized_client',
]

// ============================================================================
// COMPONENT
// ============================================================================

export function UserModal({
  isOpen,
  onClose,
  onSubmit,
  user,
  mode,
  availableRoles = DEFAULT_ROLES,
}: UserModalProps) {
  const [formData, setFormData] = useState<UserFormData>({
    email: '',
    fullName: '',
    phone: '',
    role: 'technical_assistant',
    isActive: true,
    password: '',
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState('')
  const [activityChanged, setActivityChanged] = useState(false)

  // Initialize form with user data when editing
  useEffect(() => {
    if (mode === 'edit' && user) {
      setFormData({
        email: user.email,
        fullName: user.fullName,
        phone: user.phone || '',
        role: user.role,
        isActive: user.isActive ?? undefined,
      })
    } else {
      setFormData({
        email: '',
        fullName: '',
        phone: '',
        role: availableRoles[0] ?? 'technical_assistant',
        isActive: true,
        password: '',
      })
    }
    setError('')
    setActivityChanged(false)
  }, [mode, user, isOpen])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    // Validation
    if (!formData.email.trim()) {
      setError('Email is required')
      return
    }
    if (!formData.fullName.trim()) {
      setError('Full name is required')
      return
    }
    if (!formData.email.includes('@')) {
      setError('Please enter a valid email address')
      return
    }
    if (mode === 'create' && (formData.password ?? '').length < MIN_PASSWORD_LENGTH) {
      setError(`Temporary password must be at least ${MIN_PASSWORD_LENGTH} characters`)
      return
    }

    setIsSubmitting(true)

    try {
      const { isActive, ...fields } = formData
      await onSubmit(mode === 'edit' && !activityChanged ? fields : { ...fields, isActive })
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save user')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleChange = (field: keyof UserFormData, value: string | boolean) => {
    if (field === 'isActive') setActivityChanged(true)
    setFormData(prev => ({ ...prev, [field]: value }))
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />

      {/* Modal */}
      <div className="relative bg-white dark:bg-surface-dark rounded-xl shadow-2xl border border-surface-overlay w-full max-w-md mx-4 max-h-[90vh] overflow-hidden animate-in fade-in zoom-in-95 duration-150">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-surface-overlay">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center">
              <User className="w-5 h-5 text-primary-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-text-primary">
                {mode === 'create' ? 'Add New User' : 'Edit User'}
              </h2>
              <p className="text-sm text-text-tertiary">
                {mode === 'create' ? 'Create a new user account' : 'Update user information'}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="btn btn-ghost btn-sm btn-square"
            disabled={isSubmitting}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form
          onSubmit={handleSubmit}
          className="p-4 space-y-4 overflow-y-auto max-h-[calc(90vh-140px)]"
        >
          {/* Error Alert */}
          {error && (
            <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 flex items-start gap-2">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
            </div>
          )}

          {/* Full Name */}
          <div>
            <label className="block text-sm font-medium text-text-primary mb-1.5">
              Full Name <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-tertiary" />
              <input
                type="text"
                value={formData.fullName}
                onChange={e => handleChange('fullName', e.target.value)}
                placeholder="Enter full name"
                className="input input-bordered w-full pl-10"
                disabled={isSubmitting}
                required
              />
            </div>
          </div>

          {/* Email */}
          <div>
            <label className="block text-sm font-medium text-text-primary mb-1.5">
              Email Address <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-tertiary" />
              <input
                type="email"
                value={formData.email}
                onChange={e => handleChange('email', e.target.value)}
                placeholder="user@example.com"
                className="input input-bordered w-full pl-10"
                disabled={isSubmitting || mode === 'edit'}
                required
              />
            </div>
            {mode === 'edit' && (
              <p className="text-xs text-text-tertiary mt-1">Email cannot be changed</p>
            )}
          </div>

          {/* Temporary Password (create mode only) */}
          {mode === 'create' && (
            <PasswordField
              label="Temporary Password"
              required
              minLength={8}
              autoComplete="new-password"
              value={formData.password ?? ''}
              onChange={val => handleChange('password', val)}
              placeholder="Set an initial password"
              disabled={isSubmitting}
              showRequirements={true}
              helperText="Share this with the user; they can change it after signing in."
            />
          )}

          {/* Phone */}
          <div>
            <label className="block text-sm font-medium text-text-primary mb-1.5">
              Phone Number
            </label>
            <div className="relative">
              <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-tertiary" />
              <input
                type="tel"
                pattern="^[6-9]\d{9}$"
                title="10-digit Indian mobile number starting with 6-9 (e.g. 9876543210)"
                value={formData.phone}
                onChange={e => handleChange('phone', e.target.value)}
                placeholder="9876543210"
                className="input input-bordered w-full pl-10"
                disabled={isSubmitting}
              />
            </div>
          </div>

          {/* Role */}
          <div>
            <label className="block text-sm font-medium text-text-primary mb-1.5">
              Role <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <Shield className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-tertiary" />
              <select
                value={formData.role}
                onChange={e => handleChange('role', e.target.value as UserRole)}
                className="select select-bordered w-full pl-10"
                disabled={isSubmitting}
                required
              >
                {availableRoles.map(role => (
                  <option key={role} value={role}>
                    {getRoleDisplayName(role)}
                  </option>
                ))}
              </select>
            </div>
            <p className="text-xs text-text-tertiary mt-1">
              Determines what the user can access in the system
            </p>
          </div>

          {mode === 'edit' && user?.isActive == null && !activityChanged && (
            <p className="text-sm text-text-tertiary">Activity: Not recorded</p>
          )}
          {/* Active Status (only for edit mode) */}
          {mode === 'edit' && (
            <div className="flex items-center justify-between p-3 rounded-lg bg-surface-overlay/50">
              <div>
                <p className="text-sm font-medium text-text-primary">Account Status</p>
                <p className="text-xs text-text-tertiary">
                  {formData.isActive
                    ? 'User can log in and access the system'
                    : 'User is blocked from accessing the system'}
                </p>
              </div>
              <label className="cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.isActive === true}
                  onChange={e => handleChange('isActive', e.target.checked)}
                  className="toggle toggle-primary"
                  disabled={isSubmitting}
                />
              </label>
            </div>
          )}
        </form>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-4 border-t border-surface-overlay bg-surface-overlay/30">
          <button type="button" onClick={onClose} className="btn btn-ghost" disabled={isSubmitting}>
            Cancel
          </button>
          <button
            type="submit"
            onClick={handleSubmit}
            className="btn btn-primary"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <span className="loading loading-spinner loading-sm" />
                {mode === 'create' ? 'Creating...' : 'Saving...'}
              </>
            ) : mode === 'create' ? (
              'Create User'
            ) : (
              'Save Changes'
            )}
          </button>
        </div>
      </div>
    </div>
  )
}

export default UserModal
